/** Supplier intelligence and recalls. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokInsights = {
  // ---- Supplier detail ------------------------------------------------------------------
  'stok.supplierIntel.title': 'Supplier',
  'stok.supplierIntel.subtitle':
    'Supplier facts, performance and prices from posted documents in {branch}.',
  'stok.supplierIntel.back': 'All suppliers',
  'stok.supplierIntel.open': 'Open supplier {name}',
  'stok.supplierIntel.notFound': 'This supplier does not exist or is no longer available.',
  'stok.supplierIntel.factsTitle': 'Supplier facts',
  'stok.supplierIntel.factsDesc': 'Master data shared by every branch.',
  'stok.supplierIntel.noAccess':
    'Performance, prices and ratings need the procurement read permission for this branch.',
  'stok.supplierIntel.window': 'Analysis period',
  'stok.supplierIntel.windowDays': '{days} days',
  'stok.supplierIntel.windowNote':
    '{from} – {to} (business dates). Applies to the scorecard, prices and comparison.',
  'stok.supplierIntel.range': '{from} – {to}',

  // ---- Scorecard ------------------------------------------------------------------------
  'stok.supplierIntel.scorecardTitle': 'Scorecard',
  'stok.supplierIntel.scorecardDesc':
    'Ratios over posted purchase orders, receipts and invoices of this branch, each with its sample size.',
  'stok.supplierIntel.scoreLabel': 'Overall score',
  'stok.supplierIntel.scoreValue': '{score} / 100',
  'stok.supplierIntel.scoreHint': 'Weighted from the four scored metrics.',
  'stok.supplierIntel.insufficient': 'Not enough data',
  'stok.supplierIntel.noScore':
    'No overall score: too few samples for {metrics}. It appears once every scored metric has enough data.',
  'stok.supplierIntel.weightsTitle': 'Weights used',
  'stok.supplierIntel.weightsDesc':
    'Quality = 100% − rejection rate; invoice accuracy = 100% − discrepancy rate. Price variance is shown but not scored.',
  'stok.supplierIntel.weight.fill_rate': 'Fill rate',
  'stok.supplierIntel.weight.on_time': 'On time',
  'stok.supplierIntel.weight.quality': 'Quality',
  'stok.supplierIntel.weight.invoice_accuracy': 'Invoice accuracy',
  'stok.supplierIntel.volumes':
    '{pos} POs · {receipts} receipts · {invoices} invoices · received value',
  'stok.supplierIntel.metricsLabel': 'Scorecard metrics',
  'stok.supplierIntel.metric.fill_rate': 'Fill rate',
  'stok.supplierIntel.metric.fill_rateDesc': 'Share of the ordered value that was delivered.',
  'stok.supplierIntel.metric.on_time': 'On-time delivery',
  'stok.supplierIntel.metric.on_timeDesc': 'Receipts on or before the PO expected date.',
  'stok.supplierIntel.metric.rejection_rate': 'Rejection rate',
  'stok.supplierIntel.metric.rejection_rateDesc':
    'Share of the delivered value rejected at receiving.',
  'stok.supplierIntel.metric.price_variance': 'Price variance',
  'stok.supplierIntel.metric.price_varianceDesc':
    'Invoice price against PO price (positive = billed above the PO).',
  'stok.supplierIntel.metric.invoice_discrepancy': 'Invoice discrepancy',
  'stok.supplierIntel.metric.invoice_discrepancyDesc':
    'PO invoices whose three-way match was an exception.',
  'stok.supplierIntel.samples': '{count} samples',
  'stok.supplierIntel.samplesShort': '{count} samples — needs at least {min}',
  'stok.supplierIntel.formula': 'How it is calculated',
  'stok.supplierIntel.formulaServer': 'Server definition:',
  'stok.supplierIntel.formula.fill_rate':
    'Σ received qty × PO price ÷ Σ ordered qty × PO price, over completed POs created in the period. Samples = PO lines.',
  'stok.supplierIntel.formula.on_time':
    'Posted receipts on or before the PO expected date ÷ posted receipts against POs with an expected date. Samples = receipts.',
  'stok.supplierIntel.formula.rejection_rate':
    'Σ rejected qty × price ÷ Σ delivered qty × price on posted receipt lines. Samples = receipt lines.',
  'stok.supplierIntel.formula.price_variance':
    'Σ (invoice price − PO price) × invoiced qty ÷ Σ PO price × invoiced qty on matched lines of posted invoices. Samples = lines.',
  'stok.supplierIntel.formula.invoice_discrepancy':
    'Posted PO invoices whose match was an exception (approved or not) ÷ posted PO invoices. Samples = invoices.',

  // ---- Price history --------------------------------------------------------------------
  'stok.supplierIntel.pricesTitle': 'Price history',
  'stok.supplierIntel.pricesDesc':
    'Net price per kg, litre or piece on each posted receipt, normalised from the purchase unit.',
  'stok.supplierIntel.pricesEmpty':
    'No posted receipts from this supplier in the period — prices appear after goods are received.',
  'stok.supplierIntel.priceUnit': 'Net price per {unit}',
  'stok.supplierIntel.col.receipt': 'Receipt',
  'stok.supplierIntel.col.pricePer': 'Price per {unit}',
  'stok.supplierIntel.chartLabel':
    'Price of {item} per {unit} from {from} to {to}: lowest {min}, highest {max}, latest {last}.',
  'stok.supplierIntel.chartKeyboard':
    'Price chart of {item}. Use the arrow keys to read each receipt.',
  'stok.supplierIntel.chartHint': 'Point at or focus the chart to read a receipt.',
  'stok.supplierIntel.chartPoint': '{date} · {receipt} · {price} per {unit}',
  'stok.supplierIntel.chartMax': 'High {price}',
  'stok.supplierIntel.chartMin': 'Low {price}',

  // ---- Compare --------------------------------------------------------------------------
  'stok.supplierIntel.compareTitle': 'Compare suppliers',
  'stok.supplierIntel.compareDesc':
    'Every supplier that delivered the item to this branch in the period, priced per display unit.',
  'stok.supplierIntel.compareItem': 'Item',
  'stok.supplierIntel.compareItemPick': 'Choose an item',
  'stok.supplierIntel.compareNoItem': 'Choose an item to compare its suppliers.',
  'stok.supplierIntel.compareEmpty':
    'No posted receipts of {item} in the period — the comparison appears after goods are received.',
  'stok.supplierIntel.cheapestSummary': 'Cheapest on average: {name}, per {unit}',
  'stok.supplierIntel.cheapestInsufficient': 'Based on too few receipts — check before deciding.',
  'stok.supplierIntel.cheapest': 'Cheapest',
  'stok.supplierIntel.thisSupplier': 'This supplier',
  'stok.supplierIntel.col.supplier': 'Supplier',
  'stok.supplierIntel.col.avgPer': 'Average per {unit}',
  'stok.supplierIntel.col.lastPer': 'Last per {unit}',
  'stok.supplierIntel.col.receipts': 'Receipts (samples)',
  'stok.supplierIntel.col.receivedQty': 'Accepted qty',
  'stok.supplierIntel.col.rejection': 'Rejected',
  'stok.supplierIntel.col.lastReceived': 'Last price date',

  // ---- Ratings --------------------------------------------------------------------------
  'stok.supplierIntel.ratingsTitle': 'Ratings',
  'stok.supplierIntel.ratingsDesc': 'Monthly judgement by the purchasing team, with evidence.',
  'stok.supplierIntel.ratingsEmpty':
    'No ratings yet — add one at the end of a month to record how the supplier performed.',
  'stok.supplierIntel.ratingReadOnly': 'Adding a rating needs the procurement manage permission.',
  'stok.supplierIntel.ratingAdd': 'Add rating',
  'stok.supplierIntel.ratingAddTitle': 'Rate {name}',
  'stok.supplierIntel.ratingAddDesc':
    'The rating is kept with your name and cannot be edited afterwards.',
  'stok.supplierIntel.ratingSubmit': 'Save rating',
  'stok.supplierIntel.ratingSaved': 'Rating for {period} saved.',
  'stok.supplierIntel.ratingPeriod': 'Period {period}',
  'stok.supplierIntel.ratingScore': '{score} / 5',
  'stok.supplierIntel.ratingEvidence': 'Evidence: {evidence}',
  'stok.supplierIntel.ratingBy': 'By {name} · {at}',
  'stok.supplierIntel.field.period': 'Period',
  'stok.supplierIntel.field.periodHint': 'Month rated (YYYY-MM).',
  'stok.supplierIntel.field.score': 'Score',
  'stok.supplierIntel.field.scoreHint': '1 = very poor, 5 = excellent.',
  'stok.supplierIntel.field.scorePick': 'Choose a score',
  'stok.supplierIntel.field.comment': 'Comment',
  'stok.supplierIntel.field.commentHint': 'What went well or badly (3–500 characters).',
  'stok.supplierIntel.field.evidence': 'Evidence',
  'stok.supplierIntel.field.evidenceHint': 'Document numbers, photos or notes that back the score.',
  'stok.supplierIntel.score.5': '5 — Excellent',
  'stok.supplierIntel.score.4': '4 — Good',
  'stok.supplierIntel.score.3': '3 — Adequate',
  'stok.supplierIntel.score.2': '2 — Poor',
  'stok.supplierIntel.score.1': '1 — Very poor',
  'stok.supplierIntel.err.period': 'Enter a month as YYYY-MM.',
  'stok.supplierIntel.err.score': 'Choose a score from 1 to 5.',
  'stok.supplierIntel.err.comment': 'Write a comment of at least 3 characters.',

  // ---- Recall: list and search ----------------------------------------------------------
  'stok.recall.title': 'Recall',
  'stok.recall.subtitle': 'Find affected lots in {branch}, block them and trace where they went.',
  'stok.recall.readOnly':
    'You can search and follow cases. Opening or closing a recall needs the inventory approve permission.',
  'stok.recall.searchTitle': 'Find lots',
  'stok.recall.searchDesc':
    'Search by lot code (prefix), supplier or item. Used-up lots are included for tracing.',
  'stok.recall.searchBy': 'Search by',
  'stok.recall.mode.lot_code': 'Lot code',
  'stok.recall.mode.supplier': 'Supplier',
  'stok.recall.mode.item': 'Item',
  'stok.recall.field.lot_code': 'Lot code',
  'stok.recall.field.item': 'Item',
  'stok.recall.pick': 'Choose…',
  'stok.recall.search': 'Search',
  'stok.recall.err.search': 'Enter a lot code or choose a supplier or item.',
  'stok.recall.searchIdle': 'Search to see matching lots with their stock per location.',
  'stok.recall.searchEmpty': 'No lots match — check the lot code or try the supplier or item.',
  'stok.recall.resultCount': '{count} lots found',
  'stok.recall.selectedCount': '{count} of {total} lots selected',
  'stok.recall.oneItemHint':
    'A recall case covers lots of one item: after the first lot, only lots of the same item can be selected.',
  'stok.recall.selectLot': 'Select lot {code}',
  'stok.recall.otherItem': 'Another item — open a separate case for it.',
  'stok.recall.usedUp': 'Used up',
  'stok.recall.openCase': 'Open recall case',
  'stok.recall.col.select': 'Select',
  'stok.recall.col.onHand': 'On hand',
  'stok.recall.col.case': 'Case',
  'stok.recall.col.lots': 'Lots',
  'stok.recall.col.owner': 'Owner',
  'stok.recall.col.openedAt': 'Opened',
  'stok.recall.casesTitle': 'Recall cases',
  'stok.recall.casesDesc': 'Cases of this branch, newest first.',
  'stok.recall.casesFilter': 'Case status',
  'stok.recall.filter.open': 'Open',
  'stok.recall.filter.closed': 'Closed',
  'stok.recall.filter.all': 'All',
  'stok.recall.casesEmptyOpen': 'No open recall cases. Open one from the lot search above.',
  'stok.recall.casesEmpty': 'No recall cases here yet.',
  'stok.recall.status.open': 'Open',
  'stok.recall.status.closed': 'Closed',

  // ---- Recall: open ---------------------------------------------------------------------
  'stok.recall.openTitle': 'Open recall case',
  'stok.recall.openDesc': 'Records why the lots are recalled and who follows the case up.',
  'stok.recall.selectedSummary': '{count} lots of {item}',
  'stok.recall.blockWarning':
    'The selected lots are blocked as soon as the case is opened: no FEFO use, consumption or transfer.',
  'stok.recall.field.title': 'Title',
  'stok.recall.field.titleHint': 'Short name, e.g. the supplier notice (3–120 characters).',
  'stok.recall.field.reason': 'Reason',
  'stok.recall.field.reasonHint': 'What is wrong with the lots and where the notice came from.',
  'stok.recall.field.owner': 'Owner',
  'stok.recall.field.ownerHint': 'Who follows the case up. Default: you.',
  'stok.recall.field.ownerSelfOnly': 'You own the case (choosing someone else needs staff access).',
  'stok.recall.field.ownerMe': 'Me ({name})',
  'stok.recall.field.supplier': 'Supplier',
  'stok.recall.field.noSupplier': 'No supplier',
  'stok.recall.openSubmit': 'Open case and block lots',
  'stok.recall.opening': 'Opening…',
  'stok.recall.opened': 'Recall case {number} opened.',
  'stok.recall.err.title': 'Enter a title of at least 3 characters.',
  'stok.recall.err.reason': 'Enter a reason of at least 3 characters.',

  // ---- Recall: detail -------------------------------------------------------------------
  'stok.recall.back': 'All recalls',
  'stok.recall.detailLoading': 'Recall case',
  'stok.recall.detailTitle': 'Recall {number}',
  'stok.recall.factsTitle': 'Case',
  'stok.recall.factsDesc': 'Who opened it, why, and who owns the follow-up.',
  'stok.recall.fact.opened': 'Opened',
  'stok.recall.fact.closed': 'Closed',
  'stok.recall.byAt': '{name} · {at}',
  'stok.recall.impactTitle': 'Impact per lot',
  'stok.recall.impactDesc':
    'Stock still here, wasted, consumed by orders, corrected by counts and transferred out.',
  'stok.recall.impactEmpty': 'No lots on this case.',
  'stok.recall.col.wasted': 'Wasted',
  'stok.recall.col.consumed': 'Consumed',
  'stok.recall.col.counted': 'Count adjustment',
  'stok.recall.col.transferred': 'Transferred',
  'stok.recall.orderItems': '{count} order items',
  'stok.recall.noTransfers': 'None',
  'stok.recall.transferTo': '→ {branch} · {qty}',
  'stok.recall.restrictedBranch': 'Another branch (restricted)',
  'stok.recall.restrictedBadge': 'No access to that branch',
  'stok.recall.destinationLot': 'Received into a lot there',

  // ---- Recall: close --------------------------------------------------------------------
  'stok.recall.closeTitle': 'Close the case',
  'stok.recall.closeDesc':
    'Close once the stock is dealt with. Lots stay blocked unless you release them.',
  'stok.recall.closedNote': 'This case is closed.',
  'stok.recall.closeNeedsApprove': 'Closing a recall needs the inventory approve permission.',
  'stok.recall.close': 'Close case',
  'stok.recall.closeModalTitle': 'Close recall {number}',
  'stok.recall.field.resolution': 'Resolution notes',
  'stok.recall.field.resolutionHint':
    'What was done with the stock and who was told (3–1000 characters).',
  'stok.recall.field.release': 'Release the lots back to available',
  'stok.recall.field.releaseHint':
    'Only when the recall was withdrawn, e.g. the supplier cancelled it.',
  'stok.recall.closeNext': 'Continue',
  'stok.recall.closeConfirm': 'Close recall {number}? This cannot be reopened.',
  'stok.recall.closeReleaseWarning':
    'The recalled lots become available again and can be used, consumed and transferred.',
  'stok.recall.closeKeepNote': 'The lots stay blocked as recalled.',
  'stok.recall.closeSubmit': 'Close case',
  'stok.recall.closing': 'Closing…',
  'stok.recall.closed': 'Recall case {number} closed.',
  'stok.recall.err.resolution': 'Enter resolution notes of 3 to 1000 characters.',
} as const;
